import { Disclosure, Transition } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useNavigation } from '@remix-run/react';
import { cn } from '@theprintbar/shared';
import { useEffect, useState } from 'react';

export interface AccordionProps {
	title: JSX.Element | string;
	contentClassName?: string;
	disabled?: boolean;
	open?: boolean;
	defaultOpen?: boolean;
	closeOnNavigate?: boolean;
	onOpen?: () => void;
	children: React.ReactNode;
	className?: string;
	openIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>> | React.ForwardRefExoticComponent<React.RefAttributes<SVGSVGElement>>;
	closeIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>> | React.ForwardRefExoticComponent<React.RefAttributes<SVGSVGElement>>;
	openIconClass?: string;
	closeIconClass?: string;
	disableClose?: boolean;
}
export function Accordion({
	children,
	title,
	contentClassName,
	className,
	closeOnNavigate,
	disabled,
	open,
	defaultOpen,
	disableClose,
	onOpen,
	closeIcon: CloseIcon,
	openIcon: OpenIcon,
	openIconClass,
	closeIconClass,
}: AccordionProps) {
	const [localOpen, setOpen] = useState(defaultOpen || !!open);
	const [firstRender, setFirstRender] = useState(true);

	const navigating = useNavigation().state === 'loading';

	useEffect(() => {
		if (navigating && closeOnNavigate) {
			setOpen(false);
		}
	}, [navigating]);

	useEffect(() => {
		//Skip first render
		if (firstRender) {
			setFirstRender(false);
			return;
		}
		setOpen(!!open);
	}, [open]);

	return (
		<Disclosure as="div" className={cn('w-full border-t border-blue', disabled && 'border-grey text-grey opacity-80', className)}>
			<Disclosure.Button
				className="relative flex items-center justify-between w-full py-2"
				disabled={disabled || (localOpen && disableClose)}
				onClick={() => {
					if (disableClose && localOpen) return;
					setOpen(!localOpen);
					onOpen?.();
				}}
			>
				<div className="font-light text-sm lg:text-lg normal-case">{title}</div>
				<div className="relative pr-2">
					{OpenIcon ? (
						<OpenIcon
							className={cn('transition-all duration-200 w-4 h-4', localOpen && 'rotate-180 opacity-0', openIconClass)}
						/>
					) : (
						<PlusIcon
							className={cn('transition-all duration-200 w-4 h-4', localOpen && 'rotate-180 opacity-0', openIconClass)}
						/>
					)}
					{CloseIcon ? (
						<CloseIcon
							className={cn('absolute inset-0 transition-all w-4 h-4 opacity-0', localOpen && 'opacity-100', closeIconClass)}
						/>
					) : (
						<MinusIcon
							className={cn('absolute inset-0 transition-all w-4 h-4 opacity-0', localOpen && 'opacity-100', closeIconClass)}
						/>
					)}
				</div>
			</Disclosure.Button>
			<Transition
				show={localOpen}
				enter="transition duration-100 ease-out"
				enterFrom="transform opacity-0"
				enterTo="transform opacity-100"
				leave="transition duration-75 ease-out"
				leaveFrom="transform opacity-100"
				leaveTo="transform opacity-0"
			>
				<Disclosure.Panel static className={cn('mb-6', contentClassName)}>
					{children}
				</Disclosure.Panel>
			</Transition>
		</Disclosure>
	);
}
